<template>
  <div class="compare-reports hidden-xs">
    <Modal
      v-model="state"
      title="Select a team member to compare with.."
      data-test="compare-reports-picker"
    >
      <div class="comparisons-container scrollbar">
        <div v-if="data && canCompareReport">
          <div v-for="comparison in data.comparisons" :key="comparison.reportId" class="comparison">
            <button
              class="btn btn-default"
              @click="handleSelectedComparison(comparison)"
              data-test="compare-picker-choice"
              type="button"
            >
              {{ comparison.subjectName }}
            </button>
            <div v-if="selectedComparison && selectedComparison.reportId === comparison.reportId">
              <img src="../assets/button-icons/tick-icon-pink.svg" alt="selected comparison" />
            </div>
          </div>
        </div>
        <div v-else>
          <p class="text-danger" data-test="no-available-comparisons-message">
            {{ noComparisonsAvailableMessage }}
          </p>
        </div>
      </div>
      <template #footer>
        <button
          @click="handleConfirm"
          class="btn btn-primary"
          :disabled="!selectedComparison"
          data-test="compare-picker-confirm-button"
          type="button"
        >
          Confirm
        </button>
        <button
          class="btn btn-default"
          @click="toggleState"
          data-test="compare-picker-cancel-button"
          type="button"
        >
          Cancel
        </button>
      </template>
    </Modal>
    <button
      class="btn btn-primary-compare"
      @click="toggleState"
      data-test="compare-reports-button"
      type="button"
    >
      Compare reports
    </button>
  </div>
</template>

<script setup lang="ts">
import { Modal } from "uiv";
import { computed, ref, watchEffect } from "vue";
import { debug as d } from "debug";
import { csContext } from "../services/cs-context";
import {
  ReportComparisonApiModel,
  ReportSharingTypeApiModel,
  ReportToCompareApiModel,
} from "../api-models";
import { useToggle } from "../composables/useToggle";
import { useAxios } from "../composables/api";
import { Analytics } from "../services/analytics/analytics";

const debug = d("cs.compareReports");

const props = defineProps<{
  reportEntityId: string;
  reportType: ReportSharingTypeApiModel;
  isReportOnSelf: boolean;
}>();

const canCompareReport = ref(false);

const selectedComparison = ref<ReportToCompareApiModel | null>(null);

const { state, toggleState } = useToggle(false, (newValue: boolean) => {
  const eventName = newValue ? "Clicked Open Compare Reports" : "Clicked Cancel Compare Reports";
  Analytics.action(eventName, "CompareReports", {
    isReportOnSelf: props.isReportOnSelf,
    reportType: props.reportType,
    reportEntityId: props.reportEntityId,
  });
});

const { data } = useAxios<ReportComparisonApiModel>(
  `/api/reportcreation/potential-comparisons?reportType=${props.reportType}&reportEntityId=${props.reportEntityId}`,
);

const handleSelectedComparison = (comparison: ReportToCompareApiModel): void => {
  selectedComparison.value = comparison;
};

const handleConfirm = (): void => {
  if (selectedComparison.value) {
    const url = `/SoloReport/Compare/${data.value?.first.reportType}/${props.reportEntityId}/${selectedComparison.value.reportType}/${selectedComparison.value.reportId}`;
    Analytics.actionAsync("Clicked Confirm Compare Reports", "CompareReports", {
      isReportOnSelf: props.isReportOnSelf,
      reportType: props.reportType,
      reportEntityId: props.reportEntityId,
      secondReportType: selectedComparison.value.reportType,
      secondReportEntityId: selectedComparison.value.reportId,
    })
      .then(() => {
        window.location.href = url;
      })
      .catch((e) => {
        debug(e);
        window.location.href = url;
      });
  }
};

const noComparisonsAvailableMessage = computed(() => {
  if (csContext.currentTeam) {
    return "Your team mates need to share their reports in order for you to compare with them. They can do this by going to the team settings page and selecting yes under 'Sharing your reports'.";
  }
  return "You need to be in a team to compare reports.";
});

watchEffect(() => {
  if (data.value) {
    canCompareReport.value = true;
  }
});
</script>

<style scoped lang="less">
@import (reference) "../styles/bootstrap/variables";
.compare-reports {
  margin-top: 1rem;
}
.comparisons-container {
  border-radius: 1rem;
  min-height: 10rem;
  max-height: 20rem;
  overflow-y: scroll;
  min-width: 90%;
  border: 1px solid @registration-background-color;
  padding: 2rem;
}

.comparison {
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-bottom: 1rem;
}
</style>
